import { useState, useEffect, useMemo } from 'react';
import * as DB from '@/lib/supabaseDb';
import { useToast } from '@/components/feature/Toast';
import ResponsiveFormModal from '@/components/base/ResponsiveFormModal';
import CurrencyInput from '@/components/base/CurrencyInput';
import CustomerSearchSelect from '@/components/feature/CustomerSearchSelect';
import { formatTRY } from '@/lib/currencyFormat';
import { FOLLOW_UP_STATUSES, getFollowUpStatusDef } from '@/lib/promiseStatus';

interface PaymentPromiseFormModalProps {
  open: boolean;
  onClose: () => void;
  onSaved: () => void;
  editData?: any | null;
  customers: any[];
  allSales: any[];
  promissoryNotes: any[];
  readOnly?: boolean;
  preSelectedCustomerId?: string;
  preSelectedPromissoryNoteId?: string;
  successMessage?: string;
}

export default function PaymentPromiseFormModal({
  open, onClose, onSaved, editData, customers, allSales, promissoryNotes, readOnly, preSelectedCustomerId,
  preSelectedPromissoryNoteId, successMessage,
}: PaymentPromiseFormModalProps) {
  const { showToast } = useToast();
  const isEdit = !!editData;

  const [customerId, setCustomerId] = useState('');
  const [saleId, setSaleId] = useState('');
  const [promissoryNoteId, setPromissoryNoteId] = useState('');
  const [referenceType, setReferenceType] = useState<'sale' | 'promissory_note' | ''>('');
  const [promisedAmount, setPromisedAmount] = useState('');
  const [promisedDate, setPromisedDate] = useState(new Date().toISOString().slice(0, 10));
  const [promisedTime, setPromisedTime] = useState('');
  const [contactDate, setContactDate] = useState(new Date().toISOString().slice(0, 10));
  const [nextContactDate, setNextContactDate] = useState('');
  const [contactMethod, setContactMethod] = useState('Telefon');
  const [customerStatement, setCustomerStatement] = useState('');
  const [staffNote, setStaffNote] = useState('');
  const [assignedUser, setAssignedUser] = useState('Berkay Mert');
  const [followUpStatus, setFollowUpStatus] = useState('Bekliyor');
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState(false);
  const [showAdvanced, setShowAdvanced] = useState(false);

  useEffect(() => {
    if (!open) return;
    if (editData) {
      setCustomerId(editData.customer_id || '');
      setSaleId(editData.sale_id || '');
      setPromissoryNoteId(editData.promissory_note_id || '');
      setReferenceType(editData.sale_id ? 'sale' : editData.promissory_note_id ? 'promissory_note' : '');
      setPromisedAmount(String(editData.promised_amount || ''));
      setPromisedDate(editData.promised_date || new Date().toISOString().slice(0, 10));
      setPromisedTime(editData.original_promise_time || '');
      setContactDate(editData.contact_date || new Date().toISOString().slice(0, 10));
      setNextContactDate(editData.next_contact_date || '');
      setContactMethod(editData.contact_method || 'Telefon');
      setCustomerStatement(editData.customer_statement || '');
      setStaffNote(editData.staff_note || '');
      setAssignedUser(editData.assigned_user || 'Berkay Mert');
      setFollowUpStatus(editData.follow_up_status || 'Bekliyor');
      setShowAdvanced(!!(editData.sale_id || editData.promissory_note_id));
    } else if (preSelectedCustomerId) {
      setCustomerId(preSelectedCustomerId);
      if (preSelectedPromissoryNoteId) {
        // Senet ID yalnızca opsiyonel kaynak bilgisi olarak aktarılır; kayıt buna bağımlı değildir.
        setSaleId(''); setPromissoryNoteId(preSelectedPromissoryNoteId); setReferenceType('promissory_note');
      } else {
        setSaleId(''); setPromissoryNoteId(''); setReferenceType('');
      }
      setPromisedAmount(''); setPromisedDate(new Date().toISOString().slice(0, 10));
      setPromisedTime(''); setContactDate(new Date().toISOString().slice(0, 10)); setNextContactDate('');
      setContactMethod('Telefon'); setCustomerStatement(''); setStaffNote('');
      setAssignedUser('Berkay Mert'); setFollowUpStatus('Bekliyor'); setShowAdvanced(!!preSelectedPromissoryNoteId);
    } else {
      setCustomerId(''); setSaleId(''); setPromissoryNoteId(''); setReferenceType('');
      setPromisedAmount(''); setPromisedDate(new Date().toISOString().slice(0, 10));
      setPromisedTime(''); setContactDate(new Date().toISOString().slice(0, 10)); setNextContactDate('');
      setContactMethod('Telefon'); setCustomerStatement(''); setStaffNote('');
      setAssignedUser('Berkay Mert'); setFollowUpStatus('Bekliyor'); setShowAdvanced(false);
    }
    setErrors({});
    setSaving(false);
  }, [open, editData, preSelectedCustomerId, preSelectedPromissoryNoteId]);

  const selectedCustomer = useMemo(() => customers.find(c => c.id === customerId), [customers, customerId]);
  const customerSales = useMemo(() => allSales.filter(s => s.customer_id === customerId && s.remaining_amount > 0), [allSales, customerId]);
  const customerNotes = useMemo(() => promissoryNotes.filter(n => n.customer_id === customerId && n.status !== 'Ödendi' && n.status !== 'İptal'), [promissoryNotes, customerId]);
  const customerDebt = selectedCustomer ? (selectedCustomer.total_debt_amount || 0) : 0;

  const selectedSale = useMemo(() => allSales.find(s => s.id === saleId), [allSales, saleId]);
  const selectedNote = useMemo(() => promissoryNotes.find(n => n.id === promissoryNoteId), [promissoryNotes, promissoryNoteId]);

  const contactMethods = ['Telefon', 'WhatsApp', 'Yüz Yüze', 'Diğer'];
  const personnel = ['Berkay Mert', 'Hikmet Polat', 'Kadir Polat'];

  const inputClass = 'w-full px-3 py-2.5 text-sm bg-background-50 border border-background-200 rounded-lg focus:outline-none focus:border-primary-300 focus:ring-1 focus:ring-primary-200 text-foreground-800 placeholder:text-foreground-300';
  const inputErrorClass = 'w-full px-3 py-2.5 text-sm bg-red-50/30 border border-red-400 rounded-lg focus:outline-none focus:border-red-400 focus:ring-1 focus:ring-red-200 text-foreground-800';
  const selectClass = 'w-full px-3 py-2.5 text-sm bg-background-50 border border-background-200 rounded-lg focus:outline-none focus:border-primary-300 focus:ring-1 focus:ring-primary-200 text-foreground-800 cursor-pointer';
  const labelClass = 'text-xs font-medium text-foreground-500 mb-1.5 block';

  const validate = (): boolean => {
    const errs: Record<string, string> = {};
    if (!customerId) errs.customerId = 'Müşteri seçiniz';
    if (!promisedAmount || Number(promisedAmount) <= 0) errs.promisedAmount = 'Geçerli bir tutar giriniz';
    if (!promisedDate) errs.promisedDate = 'Söz tarihi seçiniz';
    if (!contactDate) errs.contactDate = 'Görüşme tarihi seçiniz';
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleSubmit = async () => {
    if (!validate() || saving) { showToast('Lütfen zorunlu alanları doldurunuz.', 'error'); return; }
    setSaving(true);
    try {
      const data = {
        customer_id: customerId,
        sale_id: referenceType === 'sale' ? saleId : null,
        promissory_note_id: referenceType === 'promissory_note' ? promissoryNoteId : null,
        source_type: referenceType || null,
        promised_amount: Number(promisedAmount),
        promised_date: promisedDate,
        contact_date: contactDate,
        next_contact_date: nextContactDate || null,
        contact_method: contactMethod,
        customer_statement: customerStatement,
        staff_note: staffNote,
        follow_up_status: followUpStatus,
        original_promise_date: editData?.original_promise_date || promisedDate,
        original_promise_time: promisedTime || null,
        assigned_user_id: null,
        assigned_user: assignedUser,
      };

      if (isEdit) {
        const { error } = await DB.updatePaymentPromise(editData.id, data);
        if (error) { showToast(error, 'error'); setSaving(false); return; }

        await DB.createPaymentPromiseActivity({
          payment_promise_id: editData.id,
          customer_id: customerId,
          activity_type: 'Düzenlendi',
          activity_date: new Date().toISOString().split('T')[0],
          note: 'Ödeme sözü bilgileri güncellendi.',
          created_by: 'Admin',
        });

        showToast('Ödeme sözü güncellendi.', 'success');
      } else {
        const { error } = await DB.createPaymentPromise(data);
        if (error) { showToast(error, 'error'); setSaving(false); return; }
        showToast(successMessage || 'Ödeme sözü oluşturuldu.', 'success');
      }

      onSaved();
    } catch {
      showToast('Kayıt sırasında bir hata oluştu.', 'error');
    } finally {
      setSaving(false);
    }
  };

  if (!open) return null;

  const handleClose = () => { if (saving) return; onClose(); };

  const currentStatusDef = getFollowUpStatusDef(followUpStatus);

  const headerContent = (
    <div className="flex items-center justify-between px-5 py-3.5">
      <div className="flex items-center gap-3">
        <span className="w-8 h-8 rounded-lg bg-amber-500 flex items-center justify-center flex-shrink-0"><i className="ri-hand-heart-line text-background-50 text-sm"></i></span>
        <div>
          <h3 className="text-base font-semibold text-foreground-900">{readOnly ? 'Ödeme Sözü Detayı' : isEdit ? 'Ödeme Sözünü Düzenle' : 'Yeni Ödeme Sözü'}</h3>
          {selectedCustomer && <p className="text-xs text-foreground-400">{selectedCustomer.full_name} için ödeme sözü</p>}
        </div>
      </div>
      <button onClick={handleClose} disabled={saving} className="w-8 h-8 rounded-lg hover:bg-background-100 flex items-center justify-center cursor-pointer disabled:opacity-50"><i className="ri-close-line text-foreground-500"></i></button>
    </div>
  );

  const footerContent = !readOnly ? (
    <div className="flex gap-3 px-5 py-3">
      <button onClick={handleClose} disabled={saving} className="btn-outline cursor-pointer whitespace-nowrap disabled:opacity-50 text-sm">İptal</button>
      <button onClick={handleSubmit} disabled={saving} className="btn-primary flex items-center justify-center gap-2 cursor-pointer whitespace-nowrap disabled:opacity-60 text-sm flex-1 lg:flex-none">
        {saving ? <><i className="ri-loader-4-line animate-spin"></i>Kaydediliyor...</> : <><i className="ri-check-line"></i>{isEdit ? 'Güncelle' : 'Kaydet'}</>}
      </button>
    </div>
  ) : (
    <div className="flex gap-3 px-5 py-3">
      <button onClick={handleClose} className="btn-outline cursor-pointer whitespace-nowrap text-sm flex-1">Kapat</button>
    </div>
  );

  return (
    <ResponsiveFormModal
      isOpen={open}
      onClose={handleClose}
      zIndex={60}
      header={headerContent}
      footer={footerContent}
    >
      <div className="px-5 py-4 space-y-4">
        {/* Customer Selection */}
        <div>
          <CustomerSearchSelect
            value={customerId}
            onChange={(id) => { setCustomerId(id); setSaleId(''); setPromissoryNoteId(''); setReferenceType(''); setErrors({}); }}
            label="Müşteri"
            required
            error={errors.customerId}
            disabled={readOnly}
            placeholder="Müşteri ara..."
          />
        </div>

        {/* Customer Info Summary */}
        {selectedCustomer && (
          <div className="bg-background-100/50 rounded-lg border border-background-200 p-3 grid grid-cols-2 sm:grid-cols-4 gap-3">
            <div><p className="text-xs text-foreground-400">Müşteri</p><p className="text-sm font-medium text-foreground-800">{selectedCustomer.full_name}</p></div>
            <div><p className="text-xs text-foreground-400">Telefon</p><p className="text-sm font-medium text-foreground-800">{selectedCustomer.phone}</p></div>
            <div><p className="text-xs text-foreground-400">Toplam Borç</p><p className={`text-sm font-bold ${customerDebt > 0 ? 'text-red-500' : 'text-emerald-600'}`}>{formatTRY(customerDebt)}</p></div>
            <div><p className="text-xs text-foreground-400">Durum</p><p className="text-sm font-medium">{selectedCustomer.status}</p></div>
          </div>
        )}

        {/* İlgili Kayıt - opsiyonel gelişmiş alan, varsayılan akışta gizli.
            Ödeme sözü varsayılan olarak müşterinin genel borcuna bağlı çalışır;
            belirli bir satış/senede bağlamak isteyenler bu alanı açabilir. */}
        {selectedCustomer && !readOnly && (
          <button
            type="button"
            onClick={() => setShowAdvanced(v => !v)}
            className="flex items-center gap-1.5 text-xs font-medium text-foreground-400 hover:text-foreground-600 cursor-pointer"
          >
            <i className={`ri-arrow-right-s-line transition-transform ${showAdvanced ? 'rotate-90' : ''}`}></i>
            İlgili Kayıt Seç (opsiyonel — satış/senet)
          </button>
        )}
        {selectedCustomer && (showAdvanced || (readOnly && (referenceType || saleId || promissoryNoteId))) && (
          <div>
            <label className={labelClass}>İlgili Kayıt</label>
            <div className="flex gap-2 mb-2">
              <button
                onClick={() => { setReferenceType('sale'); setPromissoryNoteId(''); }}
                className={`flex-1 text-xs font-medium py-2 rounded-lg border transition-colors cursor-pointer whitespace-nowrap ${referenceType === 'sale' ? 'bg-primary-50 border-primary-300 text-primary-700' : 'bg-background-50 border-background-200 text-foreground-500 hover:bg-background-100'}`}
                disabled={readOnly}
              >
                <i className="ri-shopping-cart-2-line mr-1"></i>Satış
              </button>
              <button
                onClick={() => { setReferenceType('promissory_note'); setSaleId(''); }}
                className={`flex-1 text-xs font-medium py-2 rounded-lg border transition-colors cursor-pointer whitespace-nowrap ${referenceType === 'promissory_note' ? 'bg-primary-50 border-primary-300 text-primary-700' : 'bg-background-50 border-background-200 text-foreground-500 hover:bg-background-100'}`}
                disabled={readOnly}
              >
                <i className="ri-file-list-3-line mr-1"></i>Senet
              </button>
              {(referenceType === 'sale' || referenceType === 'promissory_note') && !readOnly && (
                <button
                  onClick={() => { setReferenceType(''); setSaleId(''); setPromissoryNoteId(''); }}
                  className="px-3 text-xs font-medium py-2 rounded-lg border border-background-200 text-foreground-400 hover:bg-background-100 cursor-pointer whitespace-nowrap"
                  title="Bağlantıyı kaldır, genel borca bağla"
                >
                  <i className="ri-close-line"></i>
                </button>
              )}
            </div>
            {referenceType === 'sale' && (
              <select value={saleId} onChange={e => setSaleId(e.target.value)} className={selectClass} disabled={readOnly}>
                <option value="">Satış seçiniz</option>
                {customerSales.map(s => (
                  <option key={s.id} value={s.id}>{s.sale_no} - Kalan: {formatTRY(s.remaining_amount)}</option>
                ))}
              </select>
            )}
            {referenceType === 'promissory_note' && (
              <select value={promissoryNoteId} onChange={e => setPromissoryNoteId(e.target.value)} className={selectClass} disabled={readOnly}>
                <option value="">Senet seçiniz</option>
                {customerNotes.map(n => {
                  const remaining = n.remaining_amount !== undefined ? n.remaining_amount : ((n.amount || 0) - (n.paid_amount || 0));
                  return (
                    <option key={n.id} value={n.id}>{n.note_no} - Kalan: {formatTRY(remaining)}</option>
                  );
                })}
              </select>
            )}
            {!referenceType && (
              <p className="text-xs text-foreground-400">Seçim yapılmazsa ödeme sözü müşterinin genel borcuna bağlı çalışır.</p>
            )}
            {errors.reference && <p className="text-xs text-red-500 mt-1">{errors.reference}</p>}
          </div>
        )}

        {/* Selected Reference Summary */}
        {selectedSale && (
          <div className="bg-amber-50/50 rounded-lg border border-amber-200 p-3 grid grid-cols-2 sm:grid-cols-4 gap-3">
            <div><p className="text-xs text-foreground-400">Satış No</p><p className="text-sm font-medium text-primary-600">{selectedSale.sale_no}</p></div>
            <div><p className="text-xs text-foreground-400">Toplam</p><p className="text-sm font-medium">{formatTRY(selectedSale.total_amount)}</p></div>
            <div><p className="text-xs text-foreground-400">Ödenen</p><p className="text-sm font-medium text-emerald-600">{formatTRY(selectedSale.paid_amount)}</p></div>
            <div><p className="text-xs text-foreground-400">Kalan</p><p className="text-sm font-bold text-red-500">{formatTRY(selectedSale.remaining_amount)}</p></div>
          </div>
        )}

        {selectedNote && (
          <div className="bg-amber-50/50 rounded-lg border border-amber-200 p-3 grid grid-cols-2 sm:grid-cols-4 gap-3">
            <div><p className="text-xs text-foreground-400">Senet No</p><p className="text-sm font-medium text-primary-600">{selectedNote.note_no}</p></div>
            <div><p className="text-xs text-foreground-400">Tutar</p><p className="text-sm font-medium">{formatTRY(selectedNote.amount || 0)}</p></div>
            <div><p className="text-xs text-foreground-400">Ödenen</p><p className="text-sm font-medium text-emerald-600">{formatTRY(selectedNote.paid_amount || 0)}</p></div>
            <div>
              <p className="text-xs text-foreground-400">Kalan</p>
              <p className="text-sm font-bold text-red-500">
                {formatTRY(selectedNote.remaining_amount !== undefined ? selectedNote.remaining_amount : ((selectedNote.amount || 0) - (selectedNote.paid_amount || 0)))}
              </p>
            </div>
          </div>
        )}

        {/* Amount & Date */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div>
            <label className={labelClass}>Söz Verilen Tutar (TL) <span className="text-red-400">*</span></label>
            <CurrencyInput value={promisedAmount} onChange={v => { setPromisedAmount(v); setErrors({}); }} className={errors.promisedAmount ? inputErrorClass : inputClass} placeholder="0" disabled={readOnly} />
            {errors.promisedAmount && <p className="text-xs text-red-500 mt-1">{errors.promisedAmount}</p>}
          </div>
          <div>
            <label className={labelClass}>Söz Verilen Tarih <span className="text-red-400">*</span></label>
            <input type="date" value={promisedDate} onChange={e => setPromisedDate(e.target.value)} className={errors.promisedDate ? inputErrorClass : inputClass} disabled={readOnly} />
            {errors.promisedDate && <p className="text-xs text-red-500 mt-1">{errors.promisedDate}</p>}
          </div>
          <div>
            <label className={labelClass}>Söz Saati</label>
            <input type="time" value={promisedTime} onChange={e => setPromisedTime(e.target.value)} className={inputClass} disabled={readOnly} />
          </div>
        </div>

        {/* Contact Info */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className={labelClass}>Görüşme Tarihi <span className="text-red-400">*</span></label>
            <input type="date" value={contactDate} onChange={e => setContactDate(e.target.value)} className={errors.contactDate ? inputErrorClass : inputClass} disabled={readOnly} />
            {errors.contactDate && <p className="text-xs text-red-500 mt-1">{errors.contactDate}</p>}
          </div>
          <div>
            <label className={labelClass}>Görüşme Yöntemi</label>
            <select value={contactMethod} onChange={e => setContactMethod(e.target.value)} className={selectClass} disabled={readOnly}>
              {contactMethods.map(m => <option key={m} value={m}>{m}</option>)}
            </select>
          </div>
        </div>

        {/* Follow-Up Status */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className={labelClass}>Sonraki Arama Tarihi</label>
            <input type="date" value={nextContactDate} onChange={e => setNextContactDate(e.target.value)} className={inputClass} disabled={readOnly} />
          </div>
          <div>
            <label className={labelClass}>Takip Durumu</label>
            {!readOnly ? (
              <select value={followUpStatus} onChange={e => setFollowUpStatus(e.target.value)} className={selectClass}>
                {FOLLOW_UP_STATUSES.map(s => (
                  <option key={s.key} value={s.key}>{s.label}</option>
                ))}
              </select>
            ) : (
              <div className={`px-3 py-2.5 text-sm bg-background-50 border border-background-200 rounded-lg flex items-center gap-2 ${currentStatusDef?.color || 'text-foreground-800'}`}>
                <i className={currentStatusDef?.icon || 'ri-question-line'}></i>
                {currentStatusDef?.label || followUpStatus}
              </div>
            )}
          </div>
        </div>

        {/* Notes */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className={labelClass}>Müşteri Açıklaması</label>
            <textarea value={customerStatement} onChange={e => setCustomerStatement(e.target.value)} rows={2} maxLength={500} className={inputClass + ' resize-none'} disabled={readOnly} placeholder="Müşterinin beyanı..." />
            <p className="text-xs text-foreground-300 mt-0.5">{customerStatement.length}/500</p>
          </div>
          <div>
            <label className={labelClass}>Personel Notu</label>
            <textarea value={staffNote} onChange={e => setStaffNote(e.target.value)} rows={2} maxLength={500} className={inputClass + ' resize-none'} disabled={readOnly} placeholder="Personel gözlemleri..." />
            <p className="text-xs text-foreground-300 mt-0.5">{staffNote.length}/500</p>
          </div>
        </div>

        {/* Assigned User */}
        <div>
          <label className={labelClass}>Görüşmeyi Yapan</label>
          <select value={assignedUser} onChange={e => setAssignedUser(e.target.value)} className={selectClass} disabled={readOnly}>
            {personnel.map(p => <option key={p} value={p}>{p}</option>)}
          </select>
        </div>

        <div className="h-4 sm:hidden" />
      </div>
    </ResponsiveFormModal>
  );
}